import { z } from 'zod';
import {
  I18nStringSchema,
  MoneySchema,
  ShortIdSchema,
  TimestampSchema,
  UidSchema,
} from './primitives.js';

/**
 * Sprint 92 — Wash subscription packages.
 *
 * The single highest-LTV mechanic in carwash retail: customer pre-pays
 * for N washes or unlimited monthly. Operator captures cash up front,
 * customer gets in the habit, retention compounds.
 *
 * Three entities:
 *
 *   1. `WashPackageSchema` — TEMPLATE the operator defines at
 *      `tenants/{tid}/outlets/{oid}/washPackages/{packageId}`. CRUD-able
 *      from /loyalty/packages. Examples:
 *        - "5 washes" — RM 50, validity 90 days, redeems against any
 *          'exterior' service.
 *        - "Unlimited monthly" — RM 99/month, auto-renew, valid for
 *          all services on this outlet.
 *
 *   2. `CustomerSubscriptionSchema` — INSTANCE issued to a specific
 *      customer at `users/{uid}/subscriptions/{subscriptionId}` (one
 *      doc per active subscription; same customer can hold multiple
 *      from different outlets / brands). Tracks remaining balance +
 *      expiry + auto-renew metadata.
 *
 *   3. `SubscriptionUsageSchema` — LEDGER entry written every time a
 *      wash is redeemed, at
 *      `users/{uid}/subscriptions/{subscriptionId}/usage/{eventId}`.
 *      Source of truth for "how many washes did the customer actually
 *      use" so revenue recognition + dispute resolution have a clean
 *      audit trail. The subscription doc's `washesRemaining` is a
 *      cached convenience; the ledger sum is canonical.
 *
 * Why model as its own collection (not a special voucher)?
 *   - Different lifecycle: vouchers are claim-and-redeem ONCE; subs
 *     redeem N times.
 *   - Different revenue recognition: a sub's cash is recognised
 *     proportional to washes used; a voucher is just a discount on
 *     someone else's payment.
 *   - Different POS flow: at checkout we check "active sub?" first
 *     and offer 1-tap deduction before falling into the standard
 *     payment-method picker.
 *
 * Commission neutrality (Sprint 80+ recruiter epic):
 *   Subscription PURCHASES (one-time cash-in) generate a normal
 *   ticket / invoice that flows through commission math the same way
 *   any wash payment does. REDEMPTIONS (wash deducted from balance)
 *   are zero-cash events that emit no commission. The ledger entry
 *   carries `paymentMethod: 'subscription'` so the agent/recruiter
 *   ledger never double-counts revenue.
 */

// ── Template ────────────────────────────────────────────────────────────

export const WashPackageKindSchema = z.enum(['count', 'unlimited']);
export type WashPackageKind = z.infer<typeof WashPackageKindSchema>;

export const WashPackageSchema = z.object({
  id: ShortIdSchema,
  /**
   * Sprint 103 — tenant-library migration. Wash packs now live at
   * `tenants/{tid}/washPackages/{id}` (NOT per-outlet). Operator defines
   * once, item available at every outlet by default. To hide from a
   * specific outlet, add that outlet's id to `excludedOutletIds`.
   *
   * See [[project-tenant-library-catalog]] + `docs/architecture/tenant-library.md`.
   */
  excludedOutletIds: z
    .array(z.string())
    .default([])
    .describe('Outlets where this pack is HIDDEN. Empty (default) = available everywhere.'),
  name: I18nStringSchema,
  description: I18nStringSchema.optional(),
  kind: WashPackageKindSchema,
  /** Required when kind === 'count'. Number of washes included. */
  washCount: z.number().int().positive().optional(),
  /** Days from issue date before the subscription expires. 0 = never. */
  validityDays: z.number().int().nonnegative().default(90),
  priceSen: MoneySchema.describe('Customer-paid price, in sen'),
  /**
   * When set, the subscription renews automatically every N months. The
   * scheduled `subscriptionRenewal` job (deferred to Sprint 93+) creates
   * a fresh billing event. Null/undefined means one-shot purchase.
   */
  recurringMonths: z.number().int().positive().optional(),
  /**
   * Service ids this subscription can redeem against. Empty array
   * means "any service on this outlet" (most common — operator
   * intends the sub as a wash-of-any-kind perk).
   */
  eligibleServiceIds: z.array(z.string()).default([]),
  /**
   * Vehicle types this sub is priced for. Empty means all types.
   * Operators with steep size-based pricing (bike vs SUV) typically
   * issue separate packages per type to keep margins predictable.
   */
  eligibleVehicleTypes: z.array(z.string()).default([]),
  /** Featured packages get a "Most Popular" badge in the customer-pwa. */
  featured: z.boolean().default(false),
  /** Inactive packages can no longer be purchased; existing customer subs keep working. */
  active: z.boolean().default(true),
  /** Optional savings hint copy ("Save RM 15 vs paying per-wash"). */
  savingsHint: I18nStringSchema.optional(),
  /** Sort order in the customer-facing list (lower = first). */
  rank: z.number().int().default(0),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type WashPackage = z.infer<typeof WashPackageSchema>;

// ── Customer-side instance ─────────────────────────────────────────────

export const SubscriptionStatusSchema = z.enum([
  'active',
  'expired',
  'cancelled',
  'paused',
  'exhausted',
]);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;

export const CustomerSubscriptionSchema = z.object({
  id: ShortIdSchema,
  /** Which operator + outlet issued this. */
  tenantId: z.string().min(1),
  outletId: z.string().min(1),
  /** Snapshot of the source package id at issue time (templates can change). */
  packageId: z.string().min(1),
  /** Frozen snapshot of the package's critical fields at issue time so a
   *  later template edit doesn't retroactively rewrite the customer's
   *  remaining balance / eligibility. */
  packageSnapshot: z.object({
    name: I18nStringSchema,
    kind: WashPackageKindSchema,
    washCount: z.number().int().positive().optional(),
    validityDays: z.number().int().nonnegative(),
    priceSen: MoneySchema,
    eligibleServiceIds: z.array(z.string()),
    eligibleVehicleTypes: z.array(z.string()),
  }),
  /** Remaining redemption count for `kind === 'count'`. Ignored for unlimited. */
  washesRemaining: z.number().int().nonnegative(),
  /** When the subscription expires (issue + validityDays). Unlimited subs use this for renewal cutoff. */
  expiresAt: TimestampSchema,
  /** Active by default; flips on exhaustion / expiry / operator cancel. */
  status: SubscriptionStatusSchema,
  /** Who issued — operator staff uid, OR 'system' for self-purchase. */
  issuedBy: UidSchema,
  /** When the customer purchased / received the subscription. */
  issuedAt: TimestampSchema,
  /** Sen amount the customer paid (priceSen at the time of purchase). */
  pricePaidSen: MoneySchema,
  /** Payment method used at issue — operator records cash / FPX / e-wallet. */
  paymentMethod: z.enum([
    'cash',
    'duitnow_qr',
    'tng',
    'grabpay',
    'card',
    'bank_transfer',
    'voucher',
    'free',
  ]),
  /** Optional reference (e.g., Billplz bill id when customer self-purchases). */
  paymentRef: z.string().optional(),
  /**
   * Auto-renew. When set, the renewal scheduled job fires on or near
   * this date to issue a fresh package + bill the customer. Null means
   * the subscription does NOT renew. Sprint 93+ wires the scheduled job.
   */
  autoRenewAt: TimestampSchema.optional(),
  cancelledAt: TimestampSchema.optional(),
  cancellationReason: z.string().max(500).optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type CustomerSubscription = z.infer<typeof CustomerSubscriptionSchema>;

// ── Usage ledger ───────────────────────────────────────────────────────

export const SubscriptionUsageSchema = z.object({
  id: ShortIdSchema,
  /** The ticket this redemption was applied to. */
  ticketId: z.string().min(1),
  /** Service id within the ticket that consumed the wash. */
  serviceId: z.string().min(1),
  /** Outlet where the redemption happened (may differ from issuing outlet if cross-outlet sharing is enabled — Phase 2). */
  outletId: z.string().min(1),
  /** Cashier / washer uid who confirmed the redemption at POS. */
  redeemedBy: UidSchema,
  redeemedAt: TimestampSchema,
  /** Snapshot of washesRemaining BEFORE this redemption — defence against double-redemption. */
  remainingBefore: z.number().int().nonnegative(),
  /** Snapshot of washesRemaining AFTER. */
  remainingAfter: z.number().int().nonnegative(),
  /** Sen value the redemption "replaced" (a normal-price wash). For revenue analytics. */
  notionalValueSen: MoneySchema.optional(),
});
export type SubscriptionUsage = z.infer<typeof SubscriptionUsageSchema>;
